<script setup lang="ts">
import type {
  CountdownBlock as CountdownBlockType,
  ViewportSize,
} from "@aswin.dev/types";
import { computed, toRef } from "vue";
import { useI18n } from "../../composables/useI18n";
import { useCountdownTimer } from "../../composables/useCountdownTimer";
import { resolveCountdownStyleComponent } from "./countdown";
import "./countdown/countdown-themes.css";

const props = defineProps<{
  block: CountdownBlockType;
  viewport: ViewportSize;
}>();

const { t } = useI18n();

const blockRef = toRef(props, "block");
const timer = useCountdownTimer(blockRef);

// Themed variants are temporarily disabled — every countdown renders as the
// Minimal Modern style. The CountdownStyle field is still on the schema and
// every themed component is still registered, so re-enabling is a one-liner:
// switch this back to `resolveCountdownStyleComponent(props.block.style)`.
const styleComponent = computed(() =>
  resolveCountdownStyleComponent("minimal"),
);

const separatorChar = computed(() => props.block.separator);
const themeKey = "minimal";
</script>

<template>
  <!-- Empty state: no target date -->
  <div
    v-if="!block.targetDate"
    class="tpl:flex tpl:items-center tpl:justify-center tpl:py-8 tpl:text-sm tpl:text-[var(--tpl-text-dim)]"
  >
    {{ t.countdown.setDate }}
  </div>

  <!-- Expired + hidden -->
  <div
    v-else-if="timer.isExpired.value && block.hideOnExpiry"
    class="tpl:flex tpl:items-center tpl:justify-center tpl:py-4 tpl:text-xs tpl:text-[var(--tpl-text-dim)] tpl:opacity-50"
  >
    {{ t.countdown.hidden }}
  </div>

  <!-- Expired with message -->
  <div
    v-else-if="timer.isExpired.value"
    :style="{
      backgroundColor: block.backgroundColor,
      fontFamily: block.fontFamily || 'inherit',
    }"
    class="tpl:py-4 tpl:text-center"
  >
    <span
      :style="{
        fontSize: `${block.digitFontSize}px`,
        color: block.digitColor,
      }"
    >
      {{ block.expiredMessage }}
    </span>
  </div>

  <!-- Active countdown — themed dispatch.
       `data-cd-theme` lets the shared `countdown-themes.css` reset
       box-sizing and line-height for every variant, isolating each
       theme from the editor's `.tpl { line-height: normal; }` cascade. -->
  <component
    v-else
    :is="styleComponent"
    :block="block"
    :viewport="viewport"
    :segments="timer.segments.value"
    :separator="separatorChar"
    :data-cd-theme="themeKey"
  />
</template>
